import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams, generatePath } from 'react-router-dom';

// Store
import { Dispatch, RootState } from '../../../../store/store';

// Components
import InteractiveButton from '../../../../components/InteractiveButton/InteractiveButton';
import { ButtonType } from '../../../../types/button-type';
import IconBin from '../../../../components/Icons/IconBin/IconBin';
import IconEditOutlined from '../../../../components/Icons/IconEditOutlined/IconEditOutlined';
import ROUTE from '../../../../constants/route-constants';
import SpinnerLoader from '../../../../components/SpinnerLoader/SpinnerLoader';
import SwitcherOne from '../../../../components/Switchers/SwitcherOne';
import { OWNER_ROLE, ROLES } from '../../../../constants/roles';
import { DateFormatter } from '../../../../utils/date-formatter';
import { DateFormats } from '../../../../enums/date-formats';

type ComponentProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const CalibrationCertificates: React.FC<ComponentProps> = ({
  controller,
  calibrationCertificates,
  getCalibrationCertificates,
  deleteCalibrationCertificate,
  updateController,
  user,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const canSeeCalcertsNotNeededAudit =
    user?.memberData?.role === OWNER_ROLE || user?.memberData?.role === ROLES.SUPERVISOR;

  useEffect(() => {
    if (id) {
      getCalibrationCertificates(id);
    }
  }, [id, getCalibrationCertificates]);

  const handleCalcertsNotNeededChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateController({ id, formData: { calcerts_not_needed: event.target.checked } });
  };

  const handleCreate = () => {
    navigate(generatePath(ROUTE.CALIBRATION_CERTIFICATE_CREATE, { id }));
  };

  const handleEdit = (certificateId: string) => {
    navigate(generatePath(ROUTE.CALIBRATION_CERTIFICATE_EDIT, { id, certificateId }));
  };

  const handleDelete = async (certificateId: string) => {
    if (window.confirm('Are you sure you want to delete this calibration certificate?')) {
      await deleteCalibrationCertificate({ id: certificateId });
    }
  };

  const formatDate = (date: string) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  return (
    <>
      <div className="p-calibration-certificates pb-4 md:pb-25">
        <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-black dark:text-white">Calibration Certificates</h3>
              <InteractiveButton
                text="Create Calibration Certificate"
                onClick={handleCreate}
                type={ButtonType.Primary}
              />
            </div>
          </div>
          {!!controller && (
            <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
              <SwitcherOne
                name="calcerts_not_needed"
                enabled={!!controller.calcerts_not_needed}
                handleChange={handleCalcertsNotNeededChange}
                label="This controller does not need a Calibration Certificate for reports."
              />
              {canSeeCalcertsNotNeededAudit && (
                <p className="mt-2 text-sm text-gray-500">
                  Changed by: {controller.calcerts_not_needed_updated_by || '--'}
                  {' | '}
                  Changed on:{' '}
                  {controller.calcerts_not_needed_updated_at
                    ? DateFormatter({
                        date: controller.calcerts_not_needed_updated_at,
                        format: DateFormats['dd-MMM yyyy HH:mm:ss a'],
                      })
                    : '--'}
                </p>
              )}
            </div>
          )}
          <div className="p-7">
            <div className="flex w-full flex-col gap-4">
              {!calibrationCertificates && <>
                <div className='flex flex-auto justify-center items-center'><SpinnerLoader /></div>
              </>}
              {calibrationCertificates && <>
                {calibrationCertificates.map((cert) => (
                  <div
                    key={cert.id}
                    className="flex items-center justify-between rounded-sm border border-stroke p-4 dark:border-strokedark"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-4 text-sm text-gray-500">
                        <span><b>Start:</b> {formatDate(cert.startAt)}</span>
                        <span><b>Due:</b> {formatDate(cert.dueAt)}</span>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleEdit(cert.id)}
                        className="text-primary hover:text-primary/80"
                      >
                        <IconEditOutlined
                          stroke='inherit'
                          height={15}
                        />
                      </button>
                      <button
                        onClick={() => handleDelete(cert.id)}
                        className="text-meta-1 hover:text-meta-1/80"
                      >
                        <IconBin />
                      </button>
                    </div>
                  </div>
                ))}
              </>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const mapState = (state: RootState) => ({
  controller: state.controllers.controller,
  calibrationCertificates: state.calibration_certificates.calibrationCertificates,
  user: state.user.profile,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getCalibrationCertificates: dispatch.calibration_certificates.getCalibrationCertificates,
  createCalibrationCertificate: dispatch.calibration_certificates.createCalibrationCertificate,
  deleteCalibrationCertificate: dispatch.calibration_certificates.deleteCalibrationCertificate,
  updateController: dispatch.controllers.updateController,
});

export default connect(mapState, mapDispatch)(CalibrationCertificates); 